/**
 * O assistente de configuração (2026-10-06): a IA sugere o cadastro de
 * classificação inteiro a partir de uma amostra, a pessoa revisa por
 * aba e aplica — pelas rotas de catálogo que já existem.
 *
 * Quatro passos: **Empresa** (escala de sentimento e logos — o que o
 * servidor precisa saber antes de sugerir, e o que o onboarding antigo
 * coletava sem ter onde guardar), **Amostra** (planilha de clipadora ou
 * histórico, arquivos de contexto, notas), **Sugestão** (um painel por
 * aba de Ajustes, com "já cadastrado" marcado e "Aplicar" por painel) e
 * **Calibrar** (uma fatia da amostra vira upload DE VERDADE, a pessoa
 * revisa na tela de menções, e a IA propõe revisões nas premissas, lado
 * a lado, com "Aplicar" pelo PATCH de sempre).
 *
 * O que decide o que gravar mora em `apply-plan.ts` e em
 * `premise-revision.ts`; aqui é só estado de tela. Nada é gravado sem
 * clique. A mini base fica no `sessionStorage` (por empresa) só pra
 * sobreviver à ida à tela de menções e à volta — é atalho, não estado:
 * sem storage o upload continua na lista de uploads.
 */

import { uploadMentionsHref } from "@/features/mentions/upload-mentions-link";
import { CompanyLogoField } from "@/features/settings/CompanyLogoField";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  ExternalLink,
  FlaskConical,
  LoaderCircle,
  Sparkles,
  Upload,
  WandSparkles
} from "lucide-react";
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ApiError,
  apiClient,
  type CalibrationUpload,
  type CompanyProfile,
  type ConfigSuggestion,
  type ConfigSuggestionRun,
  type PremiseRevisionResult,
  type UploadSummary
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { FileDrop } from "@/shared/components/FileDrop";
import { retryPauseFrom, usePolling } from "@/shared/hooks/usePolling";
import { ACTIVE_STATUSES, statusMeta } from "@/features/uploads/upload-shared";
import {
  ACTION_LABEL,
  applyRevisions,
  planRevisions,
  type RevisionItem,
  type RevisionOutcome,
  revisionSubtitle
} from "./premise-revision";
import {
  type ApplyOutcome,
  applyPanel,
  calibrationGaps,
  type Catalog,
  type PanelPlan,
  type PlanItem,
  planPanels
} from "./apply-plan";

type Step = "empresa" | "amostra" | "sugestao" | "calibrar";

const STEPS: { id: Step; label: string }[] = [
  { id: "empresa", label: "Empresa" },
  { id: "amostra", label: "Amostra" },
  { id: "sugestao", label: "Sugestão" },
  { id: "calibrar", label: "Calibrar" }
];

/** Falha de NEGÓCIO local: a empresa não tem marca principal. Era bug
 *  (consertado em 2026-10-09, teste de render): `loadCatalog` levantava
 *  um `Error` simples cuja frase ninguém mostrava — `errorMessage` só lê
 *  `ApiError` — e a pessoa via "Não foi possível gerar a sugestão" sem
 *  saber que o conserto é cadastrar a marca. */
class MissingMainBrandError extends Error {}

/** De quanto em quanto tempo a tela pergunta pela sugestão em andamento. */
const SUGGESTION_POLL_MS = 3000;

/** `errorMessage` + a frase própria da falha de negócio local. */
function failureMessage(error: unknown, fallback: string): string {
  return error instanceof MissingMainBrandError ? error.message : errorMessage(error, fallback);
}

const CALIBRATION_SIZE_DEFAULT = 60;
const CALIBRATION_SIZE_MIN = 10;
const CALIBRATION_SIZE_MAX = 200;

function calibrationStorageKey(companyId: string): string {
  return `config-assistant:calibration:${companyId}`;
}

function readStoredCalibration(companyId: string): CalibrationUpload | null {
  try {
    const raw = sessionStorage.getItem(calibrationStorageKey(companyId));
    return raw ? (JSON.parse(raw) as CalibrationUpload) : null;
  } catch {
    return null;
  }
}

function storeCalibration(companyId: string, value: CalibrationUpload | null): void {
  try {
    if (value) sessionStorage.setItem(calibrationStorageKey(companyId), JSON.stringify(value));
    else sessionStorage.removeItem(calibrationStorageKey(companyId));
  } catch {
    // Sem storage a base some ao recarregar; o upload continua na lista.
  }
}

type ConfigAssistantProps = {
  token: string;
  companyId: string;
  companyName: string;
  /** Recém-criada pelo onboarding: o texto de abertura muda. */
  isNew?: boolean;
};

export function ConfigAssistant({ token, companyId, companyName, isNew = false }: ConfigAssistantProps) {
  const [step, setStepRaw] = useState<Step>("empresa");
  const [error, setError] = useState<string | null>(null);
  // Era bug (consertado em 2026-10-09, teste de render): `error` é UM
  // estado da página inteira e trocar de passo não o limpava, então o
  // erro de gravar um painel seguia na tela de Calibrar, onde não tem a
  // ver com nada. Trocar de passo limpa o erro.
  const setStep = (next: Step) => {
    setError(null);
    setStepRaw(next);
  };

  // ── Empresa ──────────────────────────────────────────────────
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [scale, setScale] = useState<"four_level" | "three_level">("four_level");
  const [colorLogo, setColorLogo] = useState<string | null>(null);
  const [grayLogo, setGrayLogo] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const loadProfile = useCallback(async () => {
    setProfileError(null);
    try {
      const loaded = await apiClient.companyProfile(token);
      setProfile(loaded);
      setScale(loaded.polarity_scale);
      setColorLogo(loaded.color_logo);
      setGrayLogo(loaded.gray_logo);
    } catch (caught) {
      setProfileError(errorMessage(caught, "Não foi possível carregar os dados da empresa."));
    }
  }, [token]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const saveProfileAndContinue = async (event: FormEvent) => {
    event.preventDefault();
    if (!profile) return;
    setSavingProfile(true);
    setError(null);
    try {
      const changed =
        scale !== profile.polarity_scale ||
        colorLogo !== profile.color_logo ||
        grayLogo !== profile.gray_logo;
      if (changed) {
        await apiClient.updateCompanyProfile(token, {
          polarity_scale: scale,
          color_logo: colorLogo,
          gray_logo: grayLogo
        });
        setProfile({ ...profile, polarity_scale: scale, color_logo: colorLogo, gray_logo: grayLogo });
      }
      setStep("amostra");
    } catch (caught) {
      setError(errorMessage(caught, "Não foi possível salvar os dados da empresa."));
    } finally {
      setSavingProfile(false);
    }
  };

  // ── Amostra ──────────────────────────────────────────────────
  const [sampleSource, setSampleSource] = useState<"upload" | "history">(isNew ? "upload" : "history");
  const [sampleFile, setSampleFile] = useState<File | null>(null);
  const [contextFiles, setContextFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState<ConfigSuggestion | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [plans, setPlans] = useState<PanelPlan[]>([]);

  const loadCatalog = useCallback(async (): Promise<Catalog> => {
    const brands = await apiClient.listBrands(token);
    const main = brands.find(brand => brand.is_main_brand) ?? brands[0];
    if (!main) throw new MissingMainBrandError("A empresa não tem marca principal cadastrada.");
    const [
      themeGroups,
      themes,
      keyMessages,
      spokespersons,
      publisherTiers,
      focusTerms,
      classificationTerms,
      promptRules,
      citationTags
    ] = await Promise.all([
      apiClient.listThemeGroups(token),
      apiClient.listThemes(token, { brandId: main.id }),
      apiClient.listKeyMessages(token, { brandId: main.id }),
      apiClient.listSpokespersons(token, { brandId: main.id }),
      apiClient.listPublisherTiers(token),
      apiClient.listFocusTerms(token, { brandId: main.id }),
      apiClient.listClassificationTerms(token, { brandId: main.id }),
      apiClient.listPromptRules(token, { brandId: main.id }),
      apiClient.listCitationTags(token)
    ]);
    return {
      brandId: main.id,
      brandName: main.name,
      brandTerms: main.terms,
      themeGroups,
      themes,
      keyMessages,
      spokespersons,
      publisherTiers,
      focusTerms,
      classificationTerms,
      promptRules,
      citationTags
    };
  }, [token]);

  // A sugestão é pedido em segundo plano (2026-10-27): a IA leva de 45 s a
  // mais de 2 min e o balanceador corta a requisição em 60 s, então o POST
  // devolve o pedido (202) e a tela pergunta por ele até terminar. O
  // catálogo carregado ANTES do POST espera o desfecho numa ref.
  const [suggestionRunId, setSuggestionRunId] = useState<number | null>(null);
  const [suggestionPausedUntil, setSuggestionPausedUntil] = useState<number | null>(null);
  const [suggestionLoadError, setSuggestionLoadError] = useState<string | null>(null);
  const [suggestionGone, setSuggestionGone] = useState(false);
  const pendingCatalog = useRef<Catalog | null>(null);
  // Descarte por CARGA: a resposta de uma leitura que já foi superada não
  // mexe na tela.
  const suggestionLoad = useRef(0);

  // Dono único do que fazer com o pedido, venha do POST ou da leitura.
  const settleSuggestionRun = useCallback((run: ConfigSuggestionRun) => {
    if (run.status === "FINISHED" && run.result) {
      const loadedCatalog = pendingCatalog.current;
      if (loadedCatalog) {
        setCatalog(loadedCatalog);
        setSuggestion(run.result);
        setPlans(planPanels(run.result, loadedCatalog));
        setOutcomes({});
        setStep("sugestao");
      } else {
        setError("Não foi possível gerar a sugestão.");
      }
    } else if (run.status === "ERRORED" || run.status === "FINISHED") {
      setError(run.message ?? "Não foi possível gerar a sugestão.");
    } else {
      return;
    }
    pendingCatalog.current = null;
    setSuggestionRunId(null);
    setSuggesting(false);
  }, []);

  const loadSuggestionRun = useCallback(async () => {
    if (suggestionRunId === null) return;
    const load = ++suggestionLoad.current;
    try {
      const run = await apiClient.getConfigSuggestionRun(token, suggestionRunId);
      if (load !== suggestionLoad.current) return;
      setSuggestionLoadError(null);
      setSuggestionPausedUntil(null);
      settleSuggestionRun(run);
    } catch (caught) {
      if (load !== suggestionLoad.current) return;
      setSuggestionPausedUntil(retryPauseFrom(caught));
      // 404: o pedido não volta (de outra empresa, ou apagado) — para de
      // perguntar. Qualquer outra falha segue tentando no próximo tick,
      // dizendo na tela sem trocar o que já estava mostrado.
      if (caught instanceof ApiError && caught.status === 404) {
        setSuggestionGone(true);
        setSuggestionRunId(null);
        setSuggesting(false);
        pendingCatalog.current = null;
        setError("A sugestão não foi encontrada. Gere de novo.");
        return;
      }
      setSuggestionLoadError(errorMessage(caught, "Não foi possível consultar o andamento da sugestão."));
    }
  }, [token, suggestionRunId, settleSuggestionRun]);

  // UMA leitura imediata ao ter o pedido; as seguintes são do polling.
  useEffect(() => {
    if (suggestionRunId !== null) void loadSuggestionRun();
  }, [suggestionRunId, loadSuggestionRun]);

  usePolling({
    enabled: suggestionRunId !== null && !suggestionGone,
    onTick: () => void loadSuggestionRun(),
    // Sem `signature`: sem backoff. A espera é de um a três minutos e a
    // pessoa está olhando; um degrau de 12 s só atrasaria o desfecho.
    pausedUntil: suggestionPausedUntil,
    intervalMs: SUGGESTION_POLL_MS
  });

  const suggest = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuggestionLoadError(null);
    setSuggestionGone(false);
    if (sampleSource === "upload" && !sampleFile) {
      setError("Escolha a planilha da clipadora, ou peça a amostra do histórico.");
      return;
    }
    setSuggesting(true);
    try {
      // O catálogo vem ANTES da chamada paga: empresa sem marca principal
      // não tem como aplicar a sugestão, e em paralelo a IA já tinha sido
      // chamada (e cobrada) quando a falha aparecia (2026-10-09, teste de
      // render).
      const loadedCatalog = await loadCatalog();
      const run = await apiClient.suggestCompanyConfig(token, {
        sampleSource,
        sample: sampleFile,
        contextFiles,
        notes
      });
      pendingCatalog.current = loadedCatalog;
      if (run.status === "QUEUED" || run.status === "STARTED") {
        // `suggesting` segue ligado até o desfecho.
        setSuggestionRunId(run.id);
        return;
      }
      settleSuggestionRun(run);
    } catch (caught) {
      // O 503 de "IA não configurada" é do NOSSO servidor e traz código de
      // suporte; o do balanceador (API fora do ar) vem sem, e cai na frase
      // genérica do `http.ts` em vez de mandar a pessoa à Administração.
      setError(
        caught instanceof ApiError && caught.status === 503 && caught.requestId !== null
          ? "A IA não está configurada neste ambiente (Administração → Modelos de IA)."
          : failureMessage(caught, "Não foi possível gerar a sugestão.")
      );
      setSuggesting(false);
    }
  };

  // ── Sugestão ─────────────────────────────────────────────────
  const [applying, setApplying] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Record<string, ApplyOutcome>>({});

  const updateItem = (panel: string, key: string, patch: Partial<PlanItem>) => {
    setPlans(current =>
      current.map(plan =>
        plan.panel !== panel
          ? plan
          : { ...plan, items: plan.items.map(item => (item.key === key ? { ...item, ...patch } : item)) }
      )
    );
  };

  const apply = async (plan: PanelPlan) => {
    if (!catalog) return;
    setApplying(plan.panel);
    setError(null);
    try {
      const outcome = await applyPanel(apiClient, token, plan.panel, plan.items, catalog);
      setOutcomes(current => ({ ...current, [plan.panel]: outcome }));
      // O que foi gravado passa a "já cadastrado": aplicar de novo daria
      // 409, e a tela tem que dizer o que entrou.
      const applied = new Set(outcome.applied);
      setPlans(current =>
        current.map(p =>
          p.panel !== plan.panel
            ? p
            : {
                ...p,
                items: p.items.map(item =>
                  applied.has(item.key) ? { ...item, exists: true, selected: false } : item
                )
              }
        )
      );
      if (plan.panel === "themes" || plan.panel === "brand") {
        // Grupos criados e termos de marca mudam o catálogo que os
        // próximos painéis leem. Era bug (consertado em 2026-10-09, teste de
        // render): a releitura dividia o `try` da gravação, então a falha
        // dela virava "Não foi possível aplicar." com o painel logo acima
        // dizendo "N gravado(s)" — e a pessoa reaplicava o que já estava lá.
        try {
          setCatalog(await loadCatalog());
        } catch (caught) {
          setError(
            `Os itens foram gravados, mas o cadastro não pôde ser relido (${failureMessage(caught, "falha de conexão")}). Recarregue a página antes de aplicar o próximo painel.`
          );
        }
      }
    } catch (caught) {
      setError(errorMessage(caught, "Não foi possível aplicar."));
    } finally {
      setApplying(null);
    }
  };

  // ── Calibrar ─────────────────────────────────────────────────
  const [calibration, setCalibration] = useState<CalibrationUpload | null>(() =>
    readStoredCalibration(companyId)
  );
  const [calibrationSize, setCalibrationSize] = useState(CALIBRATION_SIZE_DEFAULT);
  const [creatingCalibration, setCreatingCalibration] = useState(false);
  const [calibrationUpload, setCalibrationUpload] = useState<UploadSummary | null>(null);
  const [revisionNotes, setRevisionNotes] = useState("");
  const [revising, setRevising] = useState(false);
  const [revision, setRevision] = useState<PremiseRevisionResult | null>(null);
  const [revisionItems, setRevisionItems] = useState<RevisionItem[]>([]);
  const [applyingRevision, setApplyingRevision] = useState(false);
  const [revisionOutcome, setRevisionOutcome] = useState<RevisionOutcome | null>(null);

  // Até quando o servidor pediu pra não perguntar nada (429), a falha da
  // última consulta e se o upload sumiu (404). Mesma forma do
  // `loadSummary` do detalhe do upload (2026-10-09, teste de render):
  // antes o `catch` engolia tudo, o polling seguia perguntando a cada
  // 2,5 s contra um `Retry-After` (cada pergunta, outro 429) e um upload
  // que o servidor não acha ficava "#42" sem status e sem aviso, pra sempre.
  const [calibrationPausedUntil, setCalibrationPausedUntil] = useState<number | null>(null);
  const [calibrationLoadError, setCalibrationLoadError] = useState<string | null>(null);
  const [calibrationGone, setCalibrationGone] = useState(false);

  const calibrationId = calibration?.upload_id ?? null;
  const loadCalibrationUpload = useCallback(async () => {
    if (calibrationId === null) return;
    try {
      setCalibrationUpload(await apiClient.uploadSummary(token, calibrationId));
      setCalibrationLoadError(null);
      setCalibrationPausedUntil(null);
      setCalibrationGone(false);
    } catch (caught) {
      if (caught instanceof ApiError && caught.retryAfterSeconds !== null) {
        setCalibrationPausedUntil(Date.now() + caught.retryAfterSeconds * 1000);
      }
      // 404: o upload não volta (apagado, ou de outra empresa) — para de
      // perguntar. Qualquer outra falha continua tentando no próximo
      // tick, mas agora diz na tela, mantendo o que já estava mostrado.
      if (caught instanceof ApiError && caught.status === 404) setCalibrationGone(true);
      setCalibrationLoadError(errorMessage(caught, "Não foi possível consultar o andamento da base."));
    }
  }, [token, calibrationId]);

  useEffect(() => {
    if (step === "calibrar") void loadCalibrationUpload();
  }, [step, loadCalibrationUpload]);

  const calibrationActive =
    calibrationUpload === null ? calibrationId !== null : ACTIVE_STATUSES.has(calibrationUpload.status);
  usePolling({
    enabled: step === "calibrar" && calibrationActive && !calibrationGone,
    onTick: () => void loadCalibrationUpload(),
    signature: `${calibrationUpload?.status ?? ""}:${calibrationUpload?.processed_rows ?? ""}`,
    pausedUntil: calibrationPausedUntil
  });

  const createCalibration = async () => {
    if (!sampleFile) return;
    setCreatingCalibration(true);
    setError(null);
    try {
      const created = await apiClient.createCalibrationUpload(token, {
        sample: sampleFile,
        size: calibrationSize
      });
      setCalibration(created);
      setCalibrationUpload(null);
      setCalibrationLoadError(null);
      setCalibrationPausedUntil(null);
      setCalibrationGone(false);
      storeCalibration(companyId, created);
    } catch (caught) {
      setError(errorMessage(caught, "Não foi possível gerar a base de calibração."));
    } finally {
      setCreatingCalibration(false);
    }
  };

  const forgetCalibration = () => {
    setCalibration(null);
    setCalibrationUpload(null);
    setCalibrationLoadError(null);
    setCalibrationPausedUntil(null);
    setCalibrationGone(false);
    storeCalibration(companyId, null);
  };

  const revise = async () => {
    setRevising(true);
    setError(null);
    setRevisionOutcome(null);
    try {
      const result = await apiClient.revisePremises(token, {
        uploadId: calibrationId,
        notes: revisionNotes
      });
      setRevision(result);
      setRevisionItems(planRevisions(result.revisions));
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 503 && caught.requestId !== null
          ? "A IA não está configurada neste ambiente (Administração → Modelos de IA)."
          : errorMessage(caught, "Não foi possível pedir a revisão.")
      );
    } finally {
      setRevising(false);
    }
  };

  const updateRevisionItem = (key: string, patch: Partial<RevisionItem>) => {
    setRevisionItems(items => items.map(item => (item.key === key ? { ...item, ...patch } : item)));
  };

  const applyRevision = async () => {
    setApplyingRevision(true);
    setError(null);
    try {
      // O catálogo pode não ter sido carregado (a pessoa voltou direto
      // pra cá): a marca principal é o que a criação de premissa pede.
      const current = catalog ?? (await loadCatalog());
      if (!catalog) setCatalog(current);
      const outcome = await applyRevisions(apiClient, token, revisionItems, current.brandId);
      setRevisionOutcome(outcome);
      const applied = new Set(outcome.applied);
      setRevisionItems(items =>
        items.map(item => (applied.has(item.key) ? { ...item, applied: true, selected: false } : item))
      );
    } catch (caught) {
      setError(failureMessage(caught, "Não foi possível aplicar."));
    } finally {
      setApplyingRevision(false);
    }
  };

  const selectedRevisions = revisionItems.filter(item => item.selected && !item.applied).length;
  const calibrationMeta = calibrationUpload
    ? statusMeta(calibrationUpload.status, calibrationUpload.is_advancing)
    : null;
  // O que a base ensinou sobre o CADASTRO, antes de qualquer revisão:
  // linha na fila de correção é veículo/canal sem cadastro, TV/rádio
  // sem duração ou texto que não casou termo — e com zero menção não
  // há o que revisar. Só contadores do resumo; nada de farejar mensagem.
  const calibrationFinished = calibrationUpload !== null && !ACTIVE_STATUSES.has(calibrationUpload.status);
  const calibrationPending = calibrationFinished ? calibrationUpload!.pending_correction_count : 0;
  const calibrationEmpty = calibrationFinished && calibrationUpload!.mention_count === 0;
  const gaps = calibrationGaps(catalog, suggestion !== null, outcomes);
  const calibrationAtRisk = gaps.brandWithoutTerms !== null || gaps.nothingApplied;

  const stepIndex = STEPS.findIndex(item => item.id === step);
  const pendingCount = useMemo(
    () => plans.reduce((sum, plan) => sum + plan.items.filter(i => i.selected && !i.exists).length, 0),
    [plans]
  );

  return (
    <section className="company-onboarding config-assistant" aria-labelledby="config-assistant-title">
      {/* O voltar e o título ficam na barra do topo ("← Assistente de
          configuração · Empresa"), como nas outras telas (2026-10-16). */}
      <header className="onboarding-header">
        <h1 id="config-assistant-title" className="sr-only">{companyName}</h1>
        <p>
          {isNew
            ? "A empresa foi criada. Agora a IA monta o cadastro de classificação a partir de uma amostra de publicações — você revisa e aplica."
            : "A IA sugere temas e subtemas, premissas, foco e termos a partir das publicações da empresa; você revisa e aplica nas abas de Ajustes."}
        </p>
      </header>

      {calibration && step !== "calibrar" && (
        <p className="assistant-note assistant-note-row">
          Há uma base de calibração em andamento (upload #{calibration.upload_id}).
          <button type="button" className="link-button" onClick={() => setStep("calibrar")}>
            Continuar a calibração
          </button>
        </p>
      )}

      <ol className="onboarding-steps" aria-label="Etapas do assistente">
        {STEPS.map((item, index) => (
          <li
            key={item.id}
            className={index === stepIndex ? "active" : index < stepIndex ? "complete" : ""}
          >
            <span>{index < stepIndex ? <Check size={16} /> : index + 1}</span>
            <div>
              <small>Etapa {index + 1}</small>
              <strong>{item.label}</strong>
            </div>
          </li>
        ))}
      </ol>

      <div className="onboarding-panel">
        {step === "empresa" && (
          <form onSubmit={saveProfileAndContinue}>
            <div className="onboarding-panel-title">
              <Building2 size={21} />
              <div>
                <h2>Como a empresa classifica?</h2>
                <p>A escala define quais polaridades a IA vai sugerir. O logo é opcional.</p>
              </div>
            </div>

            {profileError ? (
              <ApiState kind="error" onRetry={() => void loadProfile()}>{profileError}</ApiState>
            ) : !profile ? (
              <ApiState kind="loading">Carregando os dados da empresa…</ApiState>
            ) : (
              <>
                <fieldset className="assistant-scale">
                  <legend>Escala de sentimento</legend>
                  <label>
                    <input
                      type="radio"
                      name="scale"
                      checked={scale === "four_level"}
                      onChange={() => setScale("four_level")}
                    />
                    <span>
                      <strong>Quatro níveis</strong>
                      <small>Positivo, negativo, neutro e a favorabilidade (favorável/desfavorável).</small>
                    </span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="scale"
                      checked={scale === "three_level"}
                      onChange={() => setScale("three_level")}
                    />
                    <span>
                      <strong>Três níveis</strong>
                      <small>Só positivo, negativo e neutro.</small>
                    </span>
                  </label>
                </fieldset>

                {/* A mesma logo de Ajustes (2026-10-16): uma só, por arquivo ou
                    pela busca do Logo.dev. A escolha vai pra em cores e limpa
                    a em cinza; grava no "Continuar". */}
                <div className="ajustes-panel assistant-logo-field">
                  <CompanyLogoField
                    value={colorLogo ?? grayLogo}
                    onChange={value => {
                      setColorLogo(value);
                      setGrayLogo(null);
                    }}
                    onError={setError}
                  />
                </div>
              </>
            )}

            {error && <p className="form-error" role="alert">{error}</p>}
            <div className="onboarding-actions">
              {/* Empresa que já existe chegou aqui por Ajustes, e é pra lá
                  que volta, como os "Voltar" dos outros passos (pedido da
                  dona, 2026-10-27); a recém-criada ainda não tem cadastro, e
                  o que se oferece é pular o assistente. */}
              <Link className="secondary-button" to={`/empresas/${companyId}/configuracoes`}>
                {isNew ? (
                  "Ir direto aos ajustes"
                ) : (
                  <>
                    <ArrowLeft size={16} /> Voltar
                  </>
                )}
              </Link>
              <button className="primary-button" type="submit" disabled={!profile || savingProfile}>
                {savingProfile ? <LoaderCircle className="spin" size={16} /> : null}
                Continuar <ArrowRight size={16} />
              </button>
            </div>
          </form>
        )}

        {step === "amostra" && (
          <form onSubmit={suggest}>
            <div className="onboarding-panel-title">
              <Upload size={21} />
              <div>
                <h2>O que a IA vai ler?</h2>
                <p>
                  Uma amostra de publicações reais é o que ensina a IA sobre o cliente. Até 800 entram
                  no prompt — imprensa de tier 1 e as mais engajadas primeiro.
                </p>
              </div>
            </div>

            <fieldset className="assistant-scale">
              <legend>Amostra</legend>
              <label>
                <input
                  type="radio"
                  name="sample"
                  checked={sampleSource === "upload"}
                  onChange={() => setSampleSource("upload")}
                />
                <span>
                  <strong>Planilha da clipadora</strong>
                  <small>Qualquer template cadastrado. Lida só como amostra — nada é ingerido.</small>
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="sample"
                  checked={sampleSource === "history"}
                  onChange={() => setSampleSource("history")}
                />
                <span>
                  <strong>Histórico da empresa</strong>
                  <small>As menções mais recentes já cadastradas. Vazio numa empresa nova.</small>
                </span>
              </label>
            </fieldset>

            {sampleSource === "upload" && (
              <FileDrop
                accept=".xlsx,.xls,.csv"
                files={sampleFile ? [sampleFile] : []}
                onChange={files => setSampleFile(files[0] ?? null)}
                formats="XLSX, XLS ou CSV de uma clipadora cadastrada"
              />
            )}

            <div className="onboarding-fields" style={{ marginTop: "var(--space-5)" }}>
              <FileDrop
                className="field-wide"
                label="Arquivos de contexto (opcional)"
                multiple
                maxFiles={5}
                kind="text"
                accept=".txt,.md,.csv,.json,text/plain"
                files={contextFiles}
                onChange={setContextFiles}
                formats="TXT, MD, CSV ou JSON — até 5 arquivos"
                help="Briefing, árvore de temas, posicionamento: o que ajuda a IA a entender a empresa."
              />
              <label className="field field-wide">
                <span>Observações (opcional)</span>
                <small>
                  Variações de grafia do nome, siglas que colidem com outro sentido, exemplos de
                  conteúdo irrelevante — o que a amostra não mostra.
                </small>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder={"Ex.: CNC = Confederação Nacional do Comércio\nIgnorar posts sobre usinagem CNC"}
                />
              </label>
            </div>

            {suggesting && (
              <p className="assistant-note" role="status">
                A IA está montando a sugestão — costuma levar de um a três minutos. Fique nesta página.
              </p>
            )}
            {suggestionLoadError && <p className="form-error" role="alert">{suggestionLoadError}</p>}
            {error && <p className="form-error" role="alert">{error}</p>}
            <div className="onboarding-actions">
              <button className="secondary-button" type="button" onClick={() => setStep("empresa")}>
                <ArrowLeft size={16} /> Voltar
              </button>
              <button className="primary-button" type="submit" disabled={suggesting}>
                {suggesting ? <LoaderCircle className="spin" size={16} /> : <WandSparkles size={16} />}
                {suggesting ? "Gerando…" : "Gerar sugestão com IA"}
              </button>
            </div>
          </form>
        )}

        {step === "sugestao" && suggestion && (
          <div>
            <div className="onboarding-panel-title">
              <Sparkles size={21} />
              <div>
                <h2>Revise e aplique</h2>
                <p>
                  Sugestão baseada em {suggestion.sample_size} publicação(ões)
                  {suggestion.ai_calls > 1 ? ", em duas voltas da IA" : ""}. Cada painel grava na aba
                  correspondente de Ajustes; o que já existe aparece desmarcado.
                </p>
              </div>
            </div>

            {suggestion.notes.map(note => (
              <p className="assistant-note" key={note}>{note}</p>
            ))}
            {suggestion.dropped.length > 0 && (
              <details className="assistant-dropped">
                <summary>{suggestion.dropped.length} item(ns) da resposta da IA descartado(s)</summary>
                <ul>
                  {suggestion.dropped.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </details>
            )}

            {plans.map(plan => {
              const outcome = outcomes[plan.panel];
              const selectable = plan.items.filter(item => !item.exists);
              const selected = selectable.filter(item => item.selected).length;
              return (
                <section className="assistant-panel" key={plan.panel} aria-label={plan.label}>
                  <header className="assistant-panel-header">
                    <div>
                      <h3>{plan.label}</h3>
                      <small>
                        {selectable.length} sugerido(s) · {plan.items.length - selectable.length} já
                        cadastrado(s)
                      </small>
                    </div>
                    <div className="assistant-panel-actions">
                      <button
                        type="button"
                        className="text-button"
                        onClick={() =>
                          plan.items.forEach(item =>
                            !item.exists && updateItem(plan.panel, item.key, { selected: selected < selectable.length })
                          )
                        }
                        disabled={selectable.length === 0}
                      >
                        {selected < selectable.length ? "Marcar todos" : "Desmarcar todos"}
                      </button>
                      <button
                        type="button"
                        className="primary-button"
                        onClick={() => void apply(plan)}
                        disabled={applying !== null || selected === 0}
                      >
                        {applying === plan.panel ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}
                        Aplicar {selected > 0 ? `(${selected})` : ""}
                      </button>
                    </div>
                  </header>

                  {outcome && (
                    <p className={`assistant-outcome ${outcome.failed.length ? "with-failures" : ""}`} role="status">
                      {outcome.applied.length} gravado(s)
                      {outcome.failed.length > 0 && ` · ${outcome.failed.length} com erro`}
                    </p>
                  )}
                  {outcome?.failed.map(failure => (
                    <p className="form-error" key={failure.key}>
                      {failure.title}: {failure.message}
                    </p>
                  ))}

                  <ul className="assistant-items">
                    {plan.items.map(item => (
                      <li key={item.key} className={item.exists ? "exists" : item.selected ? "selected" : ""}>
                        <label className="assistant-item-head">
                          <input
                            type="checkbox"
                            checked={item.selected}
                            disabled={item.exists}
                            onChange={e => updateItem(plan.panel, item.key, { selected: e.target.checked })}
                          />
                          <span className="assistant-item-text">
                            {item.exists ? (
                              <span className="assistant-item-title">{item.title}</span>
                            ) : (
                              <input
                                className="assistant-item-title-input"
                                value={item.title}
                                onChange={e => updateItem(plan.panel, item.key, { title: e.target.value })}
                                aria-label={`${plan.label}: texto`}
                              />
                            )}
                            {item.subtitle && <small>{item.subtitle}</small>}
                          </span>
                          {item.exists && <span className="badge-pill inactive">já cadastrado</span>}
                        </label>
                        {!item.exists && item.body !== undefined && (
                          <label className="assistant-item-body">
                            <span>{item.bodyLabel}</span>
                            <textarea
                              rows={3}
                              value={item.body}
                              onChange={e => updateItem(plan.panel, item.key, { body: e.target.value })}
                            />
                          </label>
                        )}
                        {!item.exists && item.body2 !== undefined && (
                          <label className="assistant-item-body">
                            <span>{item.body2Label}</span>
                            <textarea
                              rows={3}
                              value={item.body2}
                              onChange={e => updateItem(plan.panel, item.key, { body2: e.target.value })}
                            />
                          </label>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}

            {error && <p className="form-error" role="alert">{error}</p>}
            <div className="onboarding-actions">
              <button className="secondary-button" type="button" onClick={() => setStep("amostra")}>
                <ArrowLeft size={16} /> Gerar de novo
              </button>
              <Link className="secondary-button" to={`/empresas/${companyId}/configuracoes`}>
                {pendingCount > 0 ? `Concluir (${pendingCount} sem aplicar)` : "Concluir"}
              </Link>
              <button className="primary-button" type="button" onClick={() => setStep("calibrar")}>
                Calibrar <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {step === "calibrar" && (
          <div>
            <div className="onboarding-panel-title">
              <FlaskConical size={21} />
              <div>
                <h2>Calibre com uma base real</h2>
                <p>
                  Uma fatia da amostra entra como upload de verdade e é classificada com a configuração que
                  você acabou de aplicar. Revise na tela de menções — marque como revisada o que a IA acertou,
                  corrija o que errou — e peça a revisão das premissas.
                </p>
              </div>
            </div>

            <section className="assistant-panel" aria-labelledby="calibration-base-title">
              <header className="assistant-panel-header">
                <div>
                  <h3 id="calibration-base-title">1. A mini base</h3>
                  <small>
                    Linha que já estava no histórico não é reclassificada; as novas entram como menções
                    comuns.
                  </small>
                </div>
              </header>
              {calibration ? (
                <div className="assistant-calibration">
                  <p>
                    Upload <strong>#{calibration.upload_id}</strong> · {calibration.rows} de{" "}
                    {calibration.sample_rows} linhas da planilha ({calibration.template})
                    {calibrationMeta && (
                      <>
                        {" "}
                        · <span className={`badge-pill ${calibrationMeta.tone}`}>{calibrationMeta.label}</span>
                      </>
                    )}
                    {calibrationUpload && ` · ${calibrationUpload.mention_count} menção(ões)`}
                  </p>
                  {calibration.message && <p className="form-error">{calibration.message}</p>}
                  {calibrationLoadError && <p className="form-error">{calibrationLoadError}</p>}
                  {calibrationEmpty && (
                    <p className="assistant-note" role="status">
                      Nenhuma linha virou menção, então não há o que revisar.
                      {calibrationPending > 0 && ` ${calibrationPending} linha(s) estão na fila de correção —`}{" "}
                      o motivo mais comum é veículo ou canal sem cadastro (a calibração usa o cadastro que a
                      empresa tem) ou TV e rádio sem duração na planilha. Veja o detalhe do upload, cadastre o
                      que falta e gere outra base.
                    </p>
                  )}
                  {!calibrationEmpty && calibrationPending > 0 && (
                    <p className="assistant-note" role="status">
                      {calibrationPending} linha(s) foram pra fila de correção e ficaram fora da base. O
                      detalhe do upload diz por quê; cadastrar o que falta e reprocessar as traz de volta.
                    </p>
                  )}
                  <div className="assistant-calibration-links">
                    <Link
                      className="primary-button"
                      to={uploadMentionsHref(companyId, calibration.upload_id)}
                    >
                      Revisar na tela de menções <ExternalLink size={16} />
                    </Link>
                    <Link
                      className="secondary-button"
                      to={`/empresas/${companyId}/uploads/${calibration.upload_id}`}
                    >
                      Detalhe do upload
                    </Link>
                    <button type="button" className="link-button" onClick={forgetCalibration}>
                      Gerar outra base
                    </button>
                  </div>
                </div>
              ) : sampleSource === "upload" && sampleFile ? (
                <div className="assistant-calibration">
                  {calibrationAtRisk && (
                    <div className="assistant-note" role="status">
                      <p>
                        <strong>Antes de gerar:</strong> a base é classificada com o cadastro que a empresa
                        tem agora.
                      </p>
                      <ul className="assistant-gaps">
                        {gaps.nothingApplied && (
                          <li>
                            Nenhum painel da sugestão foi aplicado — o que a IA sugeriu ainda não está no
                            cadastro.
                          </li>
                        )}
                        {gaps.brandWithoutTerms !== null && (
                          <li>
                            A marca principal ({gaps.brandWithoutTerms}) está sem termos: nenhuma linha vai
                            casar, e a base inteira vai pra fila de correção.
                          </li>
                        )}
                      </ul>
                      <button type="button" className="link-button" onClick={() => setStep("sugestao")}>
                        Voltar à sugestão e aplicar
                      </button>
                    </div>
                  )}
                  <label className="field assistant-calibration-size">
                    <span>Linhas da mini base</span>
                    <input
                      type="number"
                      min={CALIBRATION_SIZE_MIN}
                      max={CALIBRATION_SIZE_MAX}
                      value={calibrationSize}
                      onChange={e => setCalibrationSize(Number(e.target.value))}
                    />
                  </label>
                  <small>
                    As linhas mais relevantes de {sampleFile.name}: imprensa de tier 1 primeiro, depois
                    engajamento.
                  </small>
                  <button
                    className="primary-button"
                    type="button"
                    onClick={() => void createCalibration()}
                    disabled={
                      creatingCalibration ||
                      calibrationSize < CALIBRATION_SIZE_MIN ||
                      calibrationSize > CALIBRATION_SIZE_MAX
                    }
                  >
                    {creatingCalibration ? <LoaderCircle className="spin" size={16} /> : <Upload size={16} />}
                    {creatingCalibration
                      ? "Enviando…"
                      : calibrationAtRisk
                        ? "Gerar base de calibração mesmo assim"
                        : "Gerar base de calibração"}
                  </button>
                </div>
              ) : (
                <p className="assistant-note">
                  Você partiu do histórico: as menções já estão na tela de menções. Revise ali e peça a
                  revisão abaixo — ela lê as menções revisadas ou corrigidas mais recentes da empresa.
                </p>
              )}
            </section>

            <section className="assistant-panel" aria-labelledby="calibration-revision-title">
              <header className="assistant-panel-header">
                <div>
                  <h3 id="calibration-revision-title">2. Revisão das premissas</h3>
                  <small>
                    A IA relê as premissas de polaridade à luz do que você confirmou e corrigiu, e propõe
                    editar, criar ou desativar — você aplica o que fizer sentido.
                  </small>
                </div>
                <div className="assistant-panel-actions">
                  <button
                    className={revision ? "secondary-button" : "primary-button"}
                    type="button"
                    onClick={() => void revise()}
                    disabled={revising}
                  >
                    {revising ? <LoaderCircle className="spin" size={16} /> : <WandSparkles size={16} />}
                    {revising ? "Revisando…" : revision ? "Pedir de novo" : "Pedir revisão das premissas"}
                  </button>
                </div>
              </header>
              <label className="assistant-item-body assistant-revision-notes">
                <span>Observações (opcional)</span>
                <textarea
                  rows={2}
                  value={revisionNotes}
                  onChange={e => setRevisionNotes(e.target.value)}
                  placeholder="Ex.: matéria sobre tarifa é negativa mesmo quando cita investimento"
                />
              </label>

              {revision && (
                <>
                  <p className="assistant-revision-summary">{revision.summary}</p>
                  <p className="assistant-dropped">
                    {revision.corrected} corrigida(s) · {revision.confirmed} confirmada(s) ·{" "}
                    {revision.unreviewed} sem revisão
                    {revision.ai_calls === 0 ? " · sem chamada de IA" : ""}
                  </p>
                  {revision.notes.map(note => (
                    <p className="assistant-note" key={note}>{note}</p>
                  ))}
                  {revision.dropped.length > 0 && (
                    <details className="assistant-dropped">
                      <summary>{revision.dropped.length} proposta(s) da IA descartada(s) pelo servidor</summary>
                      <ul>
                        {revision.dropped.map(item => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </details>
                  )}

                  {revisionItems.length > 0 && (
                    <>
                      <div className="assistant-panel-actions assistant-revision-apply">
                        {revisionOutcome && (
                          <p
                            className={`assistant-outcome ${revisionOutcome.failed.length ? "with-failures" : ""}`}
                            role="status"
                          >
                            {revisionOutcome.applied.length} gravado(s)
                            {revisionOutcome.failed.length > 0 && ` · ${revisionOutcome.failed.length} com erro`}
                          </p>
                        )}
                        <button
                          type="button"
                          className="primary-button"
                          onClick={() => void applyRevision()}
                          disabled={applyingRevision || selectedRevisions === 0}
                        >
                          {applyingRevision ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}
                          Aplicar {selectedRevisions > 0 ? `(${selectedRevisions})` : ""}
                        </button>
                      </div>
                      {revisionOutcome?.failed.map(failure => (
                        <p className="form-error" key={failure.key}>
                          {failure.message}
                        </p>
                      ))}
                      <ul className="assistant-items">
                        {revisionItems.map(item => (
                          <li key={item.key} className={item.applied ? "exists" : item.selected ? "selected" : ""}>
                            <label className="assistant-item-head">
                              <input
                                type="checkbox"
                                checked={item.selected}
                                disabled={item.applied}
                                onChange={e => updateRevisionItem(item.key, { selected: e.target.checked })}
                              />
                              <span className="assistant-item-text">
                                <span className="assistant-item-title">
                                  {ACTION_LABEL[item.action]}{" "}
                                  {item.ruleId !== null ? `a premissa #${item.ruleId}` : "uma premissa"}
                                </span>
                                <small>{revisionSubtitle(item)}</small>
                              </span>
                              <span className="badge-pill inactive">
                                {item.applied ? "gravado" : ACTION_LABEL[item.action]}
                              </span>
                            </label>
                            <div className="assistant-revision-diff">
                              {item.before && (
                                <div className="assistant-revision-before">
                                  <span>Antes</span>
                                  <p>{item.before}</p>
                                </div>
                              )}
                              {item.action !== "deactivate" && (
                                <label className="assistant-item-body">
                                  <span>Depois</span>
                                  <textarea
                                    rows={3}
                                    value={item.after}
                                    disabled={item.applied}
                                    onChange={e => updateRevisionItem(item.key, { after: e.target.value })}
                                  />
                                </label>
                              )}
                            </div>
                            {item.why && <p className="assistant-revision-why">Por quê: {item.why}</p>}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              )}
            </section>

            {error && <p className="form-error" role="alert">{error}</p>}
            <div className="onboarding-actions">
              {/* Quem chega pelo atalho "Continuar a calibração" não tem
                  sugestão nesta visita, e o passo Sugestão sem ela não
                  desenha nada: a tela ficava em branco e sem botão
                  (relato da Kath na Danone, 2026-10-27). Sem sugestão, o
                  passo anterior que faz sentido é a Amostra. */}
              <button
                className="secondary-button"
                type="button"
                onClick={() => setStep(suggestion ? "sugestao" : "amostra")}
              >
                <ArrowLeft size={16} /> Voltar
              </button>
              <Link className="primary-button" to={`/empresas/${companyId}/configuracoes`}>
                Concluir <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
